import { AssetGroup } from '@play-co/timestep-core';
import { View } from '@play-co/timestep-core/ui';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import {
  dreamDriftCostumeAssets,
  dreamDriftWearPet,
} from 'src/game/dreamDrift/dreamDrift.costumes';
import { createUIPet } from 'src/game/pet/pet.utils';
import i18n from 'src/lib/i18n/i18n';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import uiConfig from 'src/lib/ui/config';
import sounds from 'src/lib/sounds';
import type { DreamDriftHero } from 'src/replicant/dreamDrift/dreamDrift.ruleset';

/** Arguments describing the rollback explained by the Dream Drift popup. */
export type PopupDreamDriftLostInitArgs =
  | {
      /** Setback copy used when the run continues from a checkpoint. */
      reason: 'setback';
      /** Hero retained by the restored Dream Drift segment. */
      hero?: DreamDriftHero;
    }
  | {
      /** Expiry copy used when the ended run returns to the beginning. */
      reason: 'expired';
      /** Hero from the Dream Drift run that expired. */
      hero?: DreamDriftHero;
    };

/** Centered setback explanation displayed before a Dream Drift map rollback. */
export default class PopupDreamDriftLost extends PopupBasicLazy {
  /** Localized heading updated for the current rollback reason. */
  private readonly titleText: LangBitmapFontTextView;

  /** Localized explanation updated for the current rollback reason. */
  private readonly messageText: LangBitmapFontTextView;

  /** Sad copy of the player's pet displayed inside the popup. */
  private readonly pet = createUIPet('sad');

  /** Reason selecting checkpoint-setback or expired-run copy. */
  private reason: PopupDreamDriftLostInitArgs['reason'] = 'setback';

  /**
   * Create the compact loss popup and its required interaction.
   *
   * @param opts Popup parent and close callback supplied by the popup registry.
   */
  constructor(opts: { superview: View; close: () => void }) {
    // Reuse the standard popup panel while keeping the map visible behind it.
    super({
      superview: opts.superview,
      width: 540,
      height: 660,
      offsetY: 20,
      skipTitle: true,
      skipMessage: true,
      darkerBg: true,
      close: opts.close,
      tracking: {
        feature: 'dream_drift',
        $subFeature: 'dream_drift_lost',
      },
      zIndex: 20000,
    });

    // The player must acknowledge the explanation before rollback continues.
    this.buttonClose.removeFromSuperview();

    // Explain the current rollback reason above the sad pet presentation.
    this.titleText = new LangBitmapFontTextView({
      superview: this.box,
      x: this.box.style.width / 2,
      y: 70,
      width: 460,
      height: 80,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      wordWrap: true,
      font: 'Body',
      size: 38,
      color: '#947372',
      localeText: () =>
        this.reason === 'expired'
          ? i18n('dreamDrift.lost.expired.title')
          : i18n('dreamDrift.lost.setback.title'),
    });

    // Display the player's existing pet species with its sad animation.
    this.pet.updateOpts({
      superview: this.box,
      x: this.box.style.width / 2,
      y: 300,
      scale: 0.9,
    });

    // Explain whether the run continues from a checkpoint or starts over.
    this.messageText = new LangBitmapFontTextView({
      superview: this.box,
      x: this.box.style.width / 2,
      y: 460,
      width: 460,
      height: 80,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      wordWrap: true,
      font: 'Body',
      size: 28,
      color: '#BF8147',
      localeText: () =>
        this.reason === 'expired'
          ? i18n('dreamDrift.lost.expired.message')
          : i18n('dreamDrift.lost.setback.message'),
    });

    // Continue into the existing map rollback presentation.
    new ButtonScaleViewWithText({
      superview: this.box,
      ...uiConfig.buttons.secondary,
      x: this.box.style.width / 2,
      y: 570,
      width: 300,
      height: 90,
      centerOnOrigin: true,
      font: 'Body',
      fontSize: 38,
      localeText: () => i18n('dreamDrift.lost.continue'),
      onClick: async () => {
        // Confirm the acknowledgement with the standard positive popup sound.
        sounds.sfx.play('ok');
        opts.close();
      },
      tracking: {
        feature: 'dream_drift',
        $subFeature: 'dream_drift_lost_continue',
      },
    });
  }

  /**
   * Configure the rollback reason and retained hero before opening.
   *
   * @param args Rollback reason and optional retained hero costume.
   * @returns Nothing.
   */
  public init(args: PopupDreamDriftLostInitArgs): void {
    // Select the copy that matches the authoritative rollback reason.
    this.reason = args.reason;

    // Retain the same hero visually when the stored selection is still supported.
    if (args.hero) dreamDriftWearPet(this.pet, args.hero);

    // Refresh both localized fields after their dynamic values are ready.
    this.titleText.notifyLocaleTextChanged();
    this.messageText.notifyLocaleTextChanged();

    // Open the configured popup after all dynamic values are ready.
    super.init({});
  }

  /**
   * Reveal the loss popup and play the setback sound once it appears.
   *
   * @returns Promise resolved after the popup reveal starts.
   */
  public override async fadeIn(): Promise<void> {
    // Load the pet assets and start revealing the configured popup.
    await super.fadeIn();

    // Pair the setback explanation with the existing loss sound.
    if (this.reason === 'setback') sounds.sfx.play('attack-lose');
  }

  /**
   * Load the hero costume pieces used by the sad pet presentation.
   *
   * @returns Asset group containing every selectable Dream Drift costume.
   */
  protected getAssetsGroup(): AssetGroup {
    // All heroes share this popup, so load the complete existing costume set.
    return new AssetGroup(dreamDriftCostumeAssets);
  }
}
